import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import {
  PrismButton,
  PrismButtonGroup,
  PrismCard,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import GeneralDropdownsDropdownYield from 'lumen/components/general/dropdowns/dropdown-yield';
import GeneralDropdownsTriggersTriggerDropdownIconButton from 'lumen/components/general/dropdowns/triggers/trigger-dropdown-icon-button';
import {
  type DataPreferences,
  isSdlyColumn,
  SDLY_COLUMNS,
  type SdlyColumn,
} from 'lumen/utils/constants/market-snapshots-constants';
import { localCopy } from 'tracked-toolbox';

export interface MarketSnapshotsDataPreferencesSignature {
  Args: {
    dataPreferences: DataPreferences;
    onSave: (preferences: DataPreferences) => void;
    showSdlyColumn: boolean;
  };
  Element: HTMLDivElement;
}

export default class MarketSnapshotsDataPreferences extends Component<MarketSnapshotsDataPreferencesSignature> {
  @localCopy('args.dataPreferences.sdlyColumn') declare sdlyColumn: SdlyColumn;

  @action
  onCancel(dropdown?: { toggleDropdown: () => void }): void {
    this.sdlyColumn = this.args.dataPreferences.sdlyColumn;
    dropdown?.toggleDropdown();
  }

  @action
  onSave(dropdown?: { toggleDropdown: () => void }): void {
    this.args.onSave({
      ...this.args.dataPreferences,
      sdlyColumn: this.sdlyColumn,
    });
    dropdown?.toggleDropdown();
  }

  @action
  onSdlyColumnChange(sdlyColumn: string | string[]): void {
    if (isSdlyColumn(sdlyColumn)) {
      this.sdlyColumn = sdlyColumn;
    }
  }

  <template>
    <div class="relative" ...attributes>
      {{#let
        (component
          GeneralDropdownsTriggersTriggerDropdownIconButton
          buttonType="secondary"
          dataTestId="data-preferences-trigger"
          iconBefore="sliders"
        )
        as |triggerComponent|
      }}
        <GeneralDropdownsDropdownYield
          @dropdownClass=".dropdown-target"
          {{! @glint-expect-error: Incorrect type }}
          @dropdownClassName="dropdown-target w-[320px] top-[40px] right-0"
          @label={{t "market.snapshots.data_preferences"}}
          @onCancel={{this.onCancel}}
          @selected={{null}}
          @triggerComponent={{triggerComponent}}
          as |dropdown|
        >
          <PrismCard>
            <:header as |Header|>
              <Header.title>
                {{t "market.snapshots.data_preferences"}}
              </Header.title>
            </:header>
            <:content>
              <div class="grid gap-400">
                {{#if @showSdlyColumn}}
                  <div class="flex items-center justify-between">
                    <span class="text-200--medium">
                      {{t "market.snapshots.sdly_column"}}
                    </span>

                    <PrismButtonGroup
                      @onChange={{this.onSdlyColumnChange}}
                      @value={{this.sdlyColumn}}
                      as |ButtonGroup|
                    >
                      <ButtonGroup.button
                        @label={{t "market.snapshots.metric_type.sdly"}}
                        @value={{SDLY_COLUMNS.SDLY}}
                      />
                      <ButtonGroup.button
                        @label={{t "market.snapshots.metric_type.net_sdly"}}
                        @value={{SDLY_COLUMNS.NET_SDLY}}
                      />
                    </PrismButtonGroup>
                  </div>
                {{/if}}
              </div>
            </:content>
            <:footer>
              <div class="flex flex-row justify-end gap-200">
                <PrismButton
                  @buttonType="secondary"
                  @label={{t "general.cancel"}}
                  {{on "click" (fn this.onCancel dropdown)}}
                />
                <PrismButton
                  @label={{t "general.save"}}
                  {{on "click" (fn this.onSave dropdown)}}
                />
              </div>
            </:footer>
          </PrismCard>
        </GeneralDropdownsDropdownYield>
      {{/let}}
    </div>
  </template>
}
